/** Seeded system names from curated syllable sets (M3 spec §3). Anchors keep their authored names. */
import { logger } from '../core/logger'
import { createRng, nextFloat, type Rng } from '../core/rng'
import type { GalaxyConfig } from '../data/galaxyConfig'
import { deriveSeed, intRange } from './seed'
import type { AnchorId } from './types'

/** Info message (with `{ name }`) when the retries ran out and a Roman numeral broke a name clash. */
export const NAME_NUMERAL = 'galaxy: name clash resolved with a numeral'

type NamesCfg = GalaxyConfig['names']

const ROMAN: readonly [number, string][] = [
  [1000, 'M'],
  [900, 'CM'],
  [500, 'D'],
  [400, 'CD'],
  [100, 'C'],
  [90, 'XC'],
  [50, 'L'],
  [40, 'XL'],
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
]

export function toRoman(n: number): string {
  let rest = n
  let out = ''
  for (const [value, glyph] of ROMAN) {
    while (rest >= value) {
      out += glyph
      rest -= value
    }
  }
  return out
}

function pick<T>(rng: Rng, list: readonly T[]): T {
  return list[Math.floor(nextFloat(rng) * list.length)]
}

function titleCase(s: string): string {
  return s
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

const MAX_LENGTH_ROLLS = 100

/**
 * One candidate name: 2–3 syllables of onset + nucleus + coda, Title-cased, re-rolled until its length is in
 * `names.length`. Inner syllables use the softer inner sets (a non-empty onset, a light coda), so vowels never
 * pile up and consonant clusters only sit at the start and the end of a name.
 */
export function makeName(rng: Rng, names: NamesCfg): string {
  let candidate = ''
  for (let roll = 0; roll < MAX_LENGTH_ROLLS; roll++) {
    const syllables = intRange(rng, names.syllables[0], names.syllables[1])
    candidate = ''
    for (let i = 0; i < syllables; i++) {
      const onset = pick(rng, i === 0 ? names.onsets : names.innerOnsets)
      const coda = pick(rng, i === syllables - 1 ? names.codas : names.innerCodas)
      candidate += onset + pick(rng, names.nuclei) + coda
    }
    if (candidate.length >= names.length[0] && candidate.length <= names.length[1]) break
  }
  return titleCase(candidate)
}

/**
 * Names every system in index order from `deriveSeed(seed, 'names')`. Anchors get their authored names;
 * other names are unique (case-insensitive): re-rolled on a clash, then given a Roman numeral as a last resort.
 */
export function nameSystems(seed: number, anchors: readonly (AnchorId | null)[], cfg: GalaxyConfig): string[] {
  const rng = createRng(deriveSeed(seed, 'names'))
  const taken = new Set(anchors.flatMap((a) => (a ? [cfg.anchors[a].name.toLowerCase()] : [])))
  return anchors.map((anchor) => {
    if (anchor) return cfg.anchors[anchor].name
    let name = makeName(rng, cfg.names)
    for (let r = 0; r < cfg.names.maxRetries && taken.has(name.toLowerCase()); r++) name = makeName(rng, cfg.names)
    if (taken.has(name.toLowerCase())) {
      let k = 2
      while (taken.has(`${name} ${toRoman(k)}`.toLowerCase())) k++
      name = `${name} ${toRoman(k)}`
      logger.info(NAME_NUMERAL, { name })
    }
    taken.add(name.toLowerCase())
    return name
  })
}
